'use client';

/**
 * useToastBus — Central toast subscriber
 * 
 * Mount ONCE at the app level (e.g., in layout.tsx or a provider).
 * Subscribes to all events defined in toast-events.ts and shows toasts
 * with built-in dedup.
 * 
 * Usage:
 *   // In layout.tsx or a provider component:
 *   function ToastBusProvider() {
 *     useToastBus();
 *     return null;
 *   }
 * 
 *   // Anywhere in the app:
 *   import { eventBus } from '@/lib/event-bus';
 *   eventBus.emit('price:updated', { drugId: 'x', price: 1000, name: 'Drug X' });
 */

import { useEffect } from 'react';
import { toast } from 'sonner';
import { eventBus } from '@/lib/event-bus';
import { toastEvents, type ToastEventConfig } from '@/lib/toast-events';

// Window-level dedup set (survives React strict mode remounts)
function getDedupSet(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  const w = window as any;
  if (!w.__toastDedup) w.__toastDedup = new Set();
  return w.__toastDedup;
}

const toastFn = {
  info: toast.info,
  success: toast.success,
  error: toast.error,
  warning: toast.warning,
} as const;

export function useToastBus() {
  useEffect(() => {
    const unsubscribers: (() => void)[] = [];

    for (const [eventName, config] of Object.entries(toastEvents)) {
      const handler = (data: any) => {
        const cfg = config as ToastEventConfig;
        
        // Dedup check
        if (cfg.dedupKey) {
          const dedupSet = getDedupSet();
          const bucket = Math.floor(Date.now() / (cfg.dedupTtl || 3000));
          const key = `${cfg.dedupKey(data)}:${bucket}`;
          if (dedupSet.has(key)) {
            console.log(`[ToastBus] Dedup skipped: ${eventName} (${key})`);
            return;
          }
          dedupSet.add(key);
          setTimeout(() => dedupSet.delete(key), cfg.dedupTtl || 3000);
        }

        // Show toast
        const message = cfg.message(data);
        toastFn[cfg.type](message);
      };

      unsubscribers.push(eventBus.on(eventName, handler));
    }

    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, []);
}
